import { useState, useEffect } from "react";
import { MainHeading } from "@/components/common/MainHeading";
import { Layout } from "@/components/Layout/Layout";
import { useRoleNavigate } from "@/hooks/useRoleNavigate";
import { RoutesLink } from "@/utility/RouteLinks";
import { PencilIcon, Trash2, Ticket, Eye, Download } from "lucide-react";
import CommonTable, { Action, TableColumn } from "@/components/common/CommonTable";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { ICoupon, ICouponListResponse } from "@/interfaces/Coupon/coupon.interface";
import { useFilter } from "@/hooks/useFilter";
import { TableFilters } from "@/components/common/TableFilters";
import { IFilter, IPageInfo } from "@/interfaces/common.interface";
import { formatDateTime, GetFilterUrl, showToastNotification, downloadExcel } from "@/utility/helper";
import Pagination from "@/components/common/Pagination";
import Button from "@/components/common/Button";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Switch } from "@/components/ui/switch";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";
import { RootState, useAppSelector } from "@/redux/store";

export const Coupon = () => {
    const navigate = useRoleNavigate();
    const [coupons, setCoupons] = useState<ICoupon[]>([]);
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<ICoupon | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const { canCreate, canEdit, canView, canDelete, canExport } = usePermissions();
    const { filters, debouncedFilters, updateFilter, handlePageChange } = useFilter<IFilter>({
        search: '',
        page: 1,
        limit: 10,
        startDate: '',
        endDate: '',
    });
    const user = useAppSelector((state: RootState) => state?.user?.user);

    const [loadingExport, setLoadingExport] = useState<boolean>(false);

    const handleExport = async () => {
        const queryParams = GetFilterUrl({
            status: filters.status,
            startDate: filters.startDate,
            endDate: filters.endDate,
            sort: filters.sort
        });
        const url = `${API_URL + API_ENDPOINT.coupons}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, 'coupons_export.xlsx');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
        },
    ]);

    const handleDateChange = (newRange: any) => {
        const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolvedRange);

        const start = resolvedRange[0]?.startDate;
        const end = resolvedRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };

    const [pageMetadata, setPageMetadata] = useState<IPageInfo>({
        limit: 10,
        page: 1,
        total: 0,
        totalPages: 0,
    });

    const fetchCoupons = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl(debouncedFilters);
            const response = await callApi<ICouponListResponse>(
                `${API_URL + API_ENDPOINT.coupons}?${queryParams}`,
                { method: 'GET' }
            );
            setCoupons(response?.data?.coupons || []);
            setPageMetadata(response?.data?.pageInfo ?? { limit: 10, page: 1, total: 0, totalPages: 0 });
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, [debouncedFilters.page, debouncedFilters.limit, debouncedFilters.search, debouncedFilters.startDate, debouncedFilters.endDate]);

    const handleStatusToggle = async (id: string, currentStatus: string) => {
        setUpdatingId(id);
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            const response = await callApi<any>(`${API_URL}${API_ENDPOINT.coupons}/${id}`, {
                method: 'PUT',
                data: { status: newStatus }
            });
            showToastNotification("success", response?.message || "Coupon status updated successfully");

            // Optimistic Update
            setCoupons(prev => prev.map(c => c.id === id ? { ...c, status: newStatus } : c));
        } catch (error) {
            ThrowError(error);
        } finally {
            setUpdatingId(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            const response = await callApi<any>(
                `${API_URL}${API_ENDPOINT.coupons}/${deleteTarget.id}`,
                { method: 'DELETE' }
            );
            showToastNotification('success', response?.message || 'Coupon removed successfully');
            setCoupons(prev => prev.filter(c => c.id !== deleteTarget.id));
            setDeleteTarget(null);
        } catch (error) {
            ThrowError(error);
        } finally {
            setDeleteLoading(false);
        }
    };

    const columns: TableColumn<ICoupon>[] = [
        {
            key: "code",
            header: "Coupon Code",
            render: (item) => (
                <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-primary/10 rounded-md text-primary">
                        <Ticket className="w-4 h-4" />
                    </div>
                    <span className="font-bold text-foreground tracking-wider uppercase">{item.code}</span>
                </div>
            )
        },
        {
            key: "name",
            header: "Campaign Name",
            render: (item) => <span className="font-medium">{item.name}</span>
        },
        {
            key: "value",
            header: "Discount",
            render: (item) => (
                <div className="font-semibold text-primary">
                    {item.type === 'percentage' ? `${item.discountValue}%` : `R${item.discountValue}`}
                </div>
            )
        },
        {
            key: "validity",
            header: "Validity",
            render: (item) => (
                <div className="flex flex-col text-[11px]">
                    <span className="text-foreground">From: {formatDateTime(item.startDate)}</span>
                    <span className="text-muted-foreground">To: {formatDateTime(item.expirationDate)}</span>
                </div>
            )
        },
        {
            key: "status",
            header: "Status",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <Switch
                        checked={item.status === 'active'}
                        onCheckedChange={() => handleStatusToggle(item.id, item.status)}
                        disabled={updatingId === item.id}
                        aria-label="Toggle coupon status"
                    />
                    <StatusBadge status={item.status} />
                </div>
            )
        }
    ];

    const getActions = (records: ICoupon): Action<ICoupon>[] => [
        ...(canView(RESOURCES.COUPON) ? [{
            text: "View",
            icon: <Eye className="w-4 h-4 text-primary cursor-pointer" />,
            onClick: (record: ICoupon) => {
                navigate(RoutesLink.ViewCoupon.replace(':id', record.code));
            }
        }] : []),
        ...(canEdit(RESOURCES.COUPON) && (records.userId === user?.id) ? [{
            text: "Edit",
            icon: <PencilIcon className="w-4 h-4 cursor-pointer" />,
            onClick: (record: ICoupon) => {
                navigate(RoutesLink.EditCoupon.replace(':id', record.code));
            }
        }] : []),
        ...(canDelete(RESOURCES.COUPON) && (records.userId === user?.id) ? [{
            text: "Delete",
            icon: <Trash2 className="w-4 h-4 text-destructive cursor-pointer" />,
            onClick: (record: ICoupon) => setDeleteTarget(record)
        }] : [])
    ];

    return (
        <>
            <Layout>
                <MainHeading
                    title="Coupons"
                    description="Create and manage checkout coupon codes"
                    actionLabel={canCreate(RESOURCES.COUPON) && "Create Coupon"}
                    onAction={() => navigate(RoutesLink.AddCoupon)}
                />

                <div className="mt-6 flex flex-col gap-4">
                    <TableFilters
                        searchTerm={filters.search}
                        onSearchChange={(val) => updateFilter({ search: val })}
                        placeholder="Search by coupon code or name..."
                    >
                        <div className="flex items-center gap-3 flex-wrap">
                            <DateRangePicker
                                dateRange={dateRange}
                                setDateRange={handleDateChange as any}
                            />
                            {canExport(RESOURCES.COUPON) && <Button
                                variant="outline"
                                onClick={() => handleExport()}
                                className="h-10 px-4 flex items-center gap-2 rounded-xl"
                                leftIcon={<Download className="w-4 h-4" />}
                                isLoading={loadingExport}
                            >
                                Export
                            </Button>}
                        </div>
                    </TableFilters>
                    <CommonTable
                        columns={columns}
                        data={coupons}
                        loading={loading}
                        emptyMessage="No coupon campaigns found."
                        actions={getActions}
                    />

                    <Pagination
                        currentPage={filters.page || 1}
                        totalPages={pageMetadata.totalPages}
                        totalItems={pageMetadata.total}
                        itemsPerPage={pageMetadata.limit}
                        onPageChange={handlePageChange}
                    />
                </div>
            </Layout>

            {/* ── Delete Confirmation Modal ── */}
            <ConfirmationModal
                open={Boolean(deleteTarget)}
                onOpenChange={(open) => !open && setDeleteTarget(null)}
                onConfirm={handleDelete}
                loading={deleteLoading}
                type="danger"
                title="Delete Coupon?"
                message={`Are you sure you want to delete the coupon "${deleteTarget?.name}" (${deleteTarget?.code})? This action cannot be undone.`}
                confirmText="Yes, Delete"
                cancelText="Cancel"
                size="lg"
            />
        </>
    )
};
